import React, { useRef } from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatPercentChange } from '@/lib/format';
import CountUp from '@/components/app/CountUp';
import useGlowField from '@/components/app/useGlowField';

/* ------------------------------------------------------------------ layout */

/** Every page uses this container, so gutters and max width never drift. */
export function Page({ children, className = '', ...rest }) {
  return (
    <div className={`px-4 md:px-8 pt-5 md:pt-8 max-w-[1600px] mx-auto ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** Every page uses this header, so titles never drift either. */
export function PageHeader({ title, subtitle, actions, className = '' }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5 md:mb-6 ${className}`}>
      <div className="min-w-0">
        <h1 className="text-[22px] md:text-[28px] font-bold tracking-tight text-dd-text leading-tight">
          {title}
        </h1>
        {subtitle && <p className="text-[13px] text-dd-muted mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2.5 flex-shrink-0 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ surfaces */

/*
 * Panel. A plain section on purpose: every page positions these with Tailwind
 * grid and display utilities, so the element that carries `className` has to be
 * the one the layout sees. The glow is an overlay drawn in ::before/::after and
 * never participates in layout.
 */
export function Card({ tone = 'plain', glow = false, field = true, className = '', children, ...rest }) {
  const ref = useGlowField(field);
  const tones = {
    plain: 'bg-dd-s1/70 border border-dd-line dd-glass',
    sunk: 'bg-dd-bg/60 border border-dd-line',
    feature: 'bg-dd-s2/75 border border-dd-line-strong dd-glass',
  };
  return (
    <section
      ref={ref}
      className={`rounded-[var(--dd-radius-lg)] ${tones[tone]} ${glow ? 'dd-glow' : ''} ${
        field ? 'dd-field' : ''
      } ${className}`}
      {...rest}
    >
      {children}
    </section>
  );
}

export function CardHeader({ title, action, className = '' }) {
  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      <h2 className="text-[14px] font-semibold text-dd-text tracking-tight">{title}</h2>
      {action}
    </div>
  );
}

export function Label({ children, className = '' }) {
  return (
    <span className={`block text-[11px] font-medium tracking-wide text-dd-muted ${className}`}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ controls */

const CONTROL =
  'h-10 rounded-[var(--dd-radius)] bg-dd-s1 border border-dd-line px-3.5 text-[13px] text-dd-text transition-colors hover:border-dd-line-strong focus:border-dd-lime/50';

export function Select({ className = '', children, ...rest }) {
  return (
    <select className={`${CONTROL} ${className}`} {...rest}>
      {children}
    </select>
  );
}

export function Input({ className = '', ...rest }) {
  return <input className={`${CONTROL} w-full ${className}`} {...rest} />;
}

export function Textarea({ className = '', ...rest }) {
  return (
    <textarea
      className={`rounded-[var(--dd-radius)] bg-dd-s1 border border-dd-line px-3.5 py-2.5 text-[13px] text-dd-text w-full transition-colors hover:border-dd-line-strong focus:border-dd-lime/50 ${className}`}
      {...rest}
    />
  );
}

/** Label + control, stacked. Used by every form in the app. */
export function Field({ label, hint, children, className = '' }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <Label className="mb-1.5">{label}</Label>
      {children}
      {hint && <span className="block text-[11px] text-dd-dim mt-1">{hint}</span>}
    </label>
  );
}

export function Button({ variant = 'primary', className = '', children, as: As = 'button', ...rest }) {
  const variants = {
    primary:
      'bg-dd-lime text-dd-bg font-bold hover:bg-dd-lime-dim dd-glow-soft hover:shadow-[0_6px_20px_-8px_rgb(var(--dd-lime-rgb)/0.65)] active:translate-y-px',
    secondary: 'bg-dd-s1 border border-dd-line text-dd-muted font-medium hover:text-dd-text hover:bg-dd-s2',
    ghost: 'text-dd-muted font-medium hover:text-dd-text hover:bg-dd-s2',
    danger: 'bg-dd-negative/10 text-dd-negative font-medium hover:bg-dd-negative/20',
  };
  return (
    <As
      className={`h-10 inline-flex items-center justify-center gap-1.5 rounded-[var(--dd-radius)] px-4 text-[13px] transition-colors disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ------------------------------------------------------------------ data */

export function DeltaBadge({ pct, comparedTo, size = 'md', className = '' }) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const positive = pct >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const compact = size === 'sm';
  return (
    <span
      className={`inline-flex ${
        compact
          ? 'items-center gap-1'
          : 'flex-col items-start gap-0.5 rounded-[var(--dd-radius-sm)] bg-dd-bg px-2.5 py-1.5'
      } ${className}`}
    >
      <span
        className={`inline-flex items-center gap-0.5 dd-num font-semibold ${
          compact ? 'text-[11px]' : 'text-[13px]'
        } ${positive ? 'text-dd-positive' : 'text-dd-negative'}`}
      >
        <Icon className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        {formatPercentChange(pct)}
      </span>
      {comparedTo && !compact && (
        <span className="text-[11px] text-dd-dim leading-none">vs. {comparedTo}</span>
      )}
    </span>
  );
}

/** Compact KPI, with an optional change against the previous period. */
export function StatTile({ icon: Icon, label, value, unit, format, delta, className = '' }) {
  return (
    <Card className={`p-4 lg:p-5 flex flex-col justify-between min-w-0 ${className}`}>
      <div className="flex flex-col justify-between h-full min-w-0">
        <div className="flex items-start justify-between gap-2">
          {Icon && <Icon className="w-[18px] h-[18px] text-dd-lime" strokeWidth={1.8} />}
          <DeltaBadge pct={delta} size="sm" />
        </div>
        <div className="min-w-0 mt-3">
          <p className="dd-num text-[26px] lg:text-[30px] font-semibold text-dd-text leading-none truncate">
            {format ? <CountUp value={value} format={format} /> : value}
            {unit && <span className="text-[15px] text-dd-muted ml-0.5">{unit}</span>}
          </p>
          <Label className="mt-1.5">{label}</Label>
        </div>
      </div>
    </Card>
  );
}

export function Tabs({ tabs, value, onChange, className = '' }) {
  return (
    <div
      className={`flex items-center gap-5 border-b border-dd-line overflow-x-auto dd-no-scrollbar ${className}`}
      role="tablist"
    >
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={`relative -mb-px pb-2.5 text-[13px] font-medium whitespace-nowrap transition-colors ${
              active ? 'text-dd-lime' : 'text-dd-muted hover:text-dd-text'
            }`}
          >
            {tab.label}
            <span
              className={`absolute inset-x-0 -bottom-px h-0.5 rounded-full transition-opacity ${
                active ? 'bg-dd-lime opacity-100' : 'opacity-0'
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export function MeterRow({ label, value, pct, tone = 'lime' }) {
  const tones = { lime: 'bg-dd-lime', muted: 'bg-dd-muted', dim: 'bg-dd-line-strong' };
  const width = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="flex items-center gap-3">
      <span className="w-[78px] flex-shrink-0 text-[12px] text-dd-muted">{label}</span>
      <span className="flex-1 h-1.5 rounded-full bg-dd-s3 overflow-hidden">
        <span
          className={`block h-full rounded-full ${tones[tone]} transition-[width] duration-700`}
          style={{ width: `${width}%` }}
        />
      </span>
      <span className="dd-num text-[12px] text-dd-text whitespace-nowrap">{value}</span>
    </div>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6">
      <p className="text-dd-text text-sm font-semibold">{title}</p>
      {hint && <p className="text-dd-muted text-[13px] mt-1.5 max-w-xs">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function SkeletonBar({ className = '' }) {
  return <div className={`rounded-[var(--dd-radius-sm)] bg-dd-s2 animate-pulse ${className}`} />;
}
